import posthog from "posthog-js"
import { router } from "@inertiajs/react"

declare global {
	interface Window {
		analyticsData: {
			posthog: {
				apiKey: string | null
				host: string
				sessionRecordingEnabled: boolean
			}
			user: {
				id: number | null
				email: string | null
				name: string | null
				portalAccountId: number | null
				createdAt: string | null
				updatedAt: string | null
				type: string
			}
		}
	}
}

interface Vendor {
	id: number
	name: string
}

export default class Analytics {
	private static initialized = false
	private static lastTrackedUrl: string | null = null

	static install() {
		const config = window.analyticsData?.posthog

		if (!config?.apiKey) {
			return
		}

		posthog.init(config.apiKey, {
			api_host: config.host,
			capture_pageview: false, // Manual tracking to avoid duplicates from polling
			respect_dnt: true,
			persistence: "localStorage+cookie",
			disable_session_recording: !config.sessionRecordingEnabled,
		})

		this.initialized = true

		const user = window.analyticsData.user
		if (user?.id) {
			this.identify(user)
		}

		// Track initial pageview
		this.trackPageview()

		// Track pageviews on navigation (but not on reload/polling)
		router.on("navigate", (event) => {
			const newUrl = event.detail.page.url
			if (newUrl !== this.lastTrackedUrl) {
				this.trackPageview()
			}
		})
	}

	private static trackPageview() {
		if (!this.initialized) return
		this.lastTrackedUrl = window.location.pathname + window.location.search
		posthog.capture("$pageview")
	}

	static track(event: string, properties?: Record<string, unknown>) {
		if (!this.initialized) return
		posthog.capture(event, properties)
	}

	static identify(user: Window["analyticsData"]["user"], vendor?: Vendor) {
		if (!this.initialized) return

		if (user.id) {
			posthog.identify(String(user.id), {
				name: user.name,
				email: user.email,
				type: user.type,
				portalAccountId: user.portalAccountId,
				createdAt: user.createdAt,
				updatedAt: user.updatedAt,
			})

			if (vendor?.id) {
				posthog.group("vendor", String(vendor.id), {
					name: vendor.name,
				})
			}
		} else {
			posthog.reset()
		}
	}

	static reset() {
		if (!this.initialized) return
		posthog.reset()
	}
}
